import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, SITE } from "../../content/site";

export const Navbar = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const overHero = pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        overHero ? "bg-transparent border-b border-transparent" : "bg-[#F9F8F3]/85 backdrop-blur-xl border-b border-[#E5E1D3]"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-5 md:px-10">
        <Link to="/" data-testid="solitaire-nav-logo" aria-label="Solitaire India home">
          <Logo tone={overHero ? "light" : "dark"} />
        </Link>
        <nav className="hidden lg:flex items-center gap-8" aria-label="Main">
          {NAV.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              data-testid={`nav-link-${n.id}`}
              className={({ isActive }) =>
                `link-underline text-[0.85rem] font-medium transition-colors ${overHero ? "text-white/90 hover:text-white" : "text-[#082619]"} ${isActive ? "!bg-[length:100%_1px]" : ""}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-enquire-button"
            className="hidden sm:inline-flex items-center rounded-full bg-[#74DC26] px-5 py-2.5 text-sm font-semibold text-[#082619] transition-colors hover:bg-[#082619] hover:text-[#74DC26]"
          >
            Enquire Now
          </Link>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((o) => !o)}
            className={`lg:hidden grid h-11 w-11 place-items-center rounded-full border ${overHero && !open ? "border-white/40 text-white" : "border-[#082619]/20 text-[#082619]"}`}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden border-t border-[#E5E1D3] bg-[#F9F8F3] px-5 pb-8 pt-4"
          data-testid="nav-mobile-menu"
        >
          {[{ id: "home", label: "Home", to: "/" }, ...NAV, { id: "contact", label: "Contact Us", to: "/contact" }].map((n) => (
            <NavLink key={n.id} to={n.to} data-testid={`mobile-nav-link-${n.id}`} className="font-display flex items-center justify-between border-b border-[#E5E1D3] py-4 text-2xl font-medium text-[#082619]">
              {n.label}
            </NavLink>
          ))}
          <a href={SITE.phoneHref} className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#082619]" data-testid="mobile-nav-phone">
            <Phone className="h-4 w-4" /> {SITE.phone}
          </a>
        </motion.div>
      )}
    </header>
  );
};
